import { MouseEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { AgreementSpaceReadModel } from '@client/api';
import {
  getBuildingId,
  isDefinedString,
  TableCell,
  TableRow,
  Text,
  toMoneyStringOrUndefined,
  toPercent,
  toPriceString,
  useAreaMetricString,
  useTotalAmountString,
} from '@client/toolkit';

import { AgreementSpaceAddress } from './agreement-space-address.component';

import { PortfolioPaths } from '../../../../portfolio/portfolio.router';

type AgreementSpaceItemProps = {
  agreementSpace?: AgreementSpaceReadModel;
  isFetching?: boolean;
};

export function AgreementSpaceItem({ agreementSpace, isFetching }: AgreementSpaceItemProps) {
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');
  const navigate = useNavigate();

  const handleClick = useCallback(
    (e: MouseEvent<HTMLTableRowElement>) => {
      if (agreementSpace?.space.path == null) return;
      if (agreementSpace?.space.id == null) return;

      const buildingId = getBuildingId(agreementSpace.space.path);

      const path = generatePath(PortfolioPaths.UnspecificSpace, {
        buildingId,
        spaceId: agreementSpace.space.id,
      });

      if (e.metaKey || e.ctrlKey || e.button === 1) {
        window.open(path, '_blank');
      } else {
        navigate(path);
      }
    },
    [agreementSpace?.space.path, agreementSpace?.space.id, navigate],
  );

  const totalAmount = useTotalAmountString(agreementSpace?.yearlyTotalAmount, agreementSpace?.oneTimeTotalAmount);
  const totalArea = useAreaMetricString(agreementSpace?.space?.area);
  // Create an AreaValue-like object from the override area number
  const overrideAreaValue =
    agreementSpace?.overrideArea != null ? { metric: agreementSpace.overrideArea, imperial: 0 } : undefined;
  const overrideArea = useAreaMetricString(overrideAreaValue);
  const commonAreaShare = useAreaMetricString(
    agreementSpace?.commonAreaShare != null ? { metric: agreementSpace.commonAreaShare, imperial: 0 } : undefined,
  );
  const calculatedLettableArea = useAreaMetricString(
    agreementSpace?.calculatedLettableArea != null
      ? { metric: agreementSpace.calculatedLettableArea, imperial: 0 }
      : undefined,
  );
  const commonAreaAddOnFactor =
    agreementSpace?.commonAreaAddOnFactor != null ? toPercent(agreementSpace.commonAreaAddOnFactor / 100) : undefined;
  const overrideAreaAddOnFactor =
    agreementSpace?.overrideAreaAddOnFactor != null
      ? toPercent(agreementSpace.overrideAreaAddOnFactor / 100)
      : undefined;
  const productName = agreementSpace?.price?.product?.name;
  const productPrice = toPriceString(agreementSpace?.price);
  const overwrittenPrice = agreementSpace?.overwrittenUnitAmount
    ? toMoneyStringOrUndefined(agreementSpace.overwrittenUnitAmount)
    : undefined;
  const spaceType = agreementSpace?.space?.discriminator
    ? tType(`SpaceDiscriminator.${agreementSpace.space.discriminator}`)
    : undefined;

  return (
    <TableRow verticalAlign="middle" hover onClick={handleClick}>
      {/* Navn */}
      <TableCell loading={isFetching}>
        <AgreementSpaceAddress
          name={agreementSpace?.space?.name}
          address={agreementSpace?.space?.address?.formattedAddress}
        />
      </TableCell>

      {/* Type */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedString(spaceType)}>
          {spaceType}
        </Text>
      </TableCell>

      {/* Areal */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedString(totalArea)}>
          {totalArea}
        </Text>
      </TableCell>

      {/* Andel fellesareal */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedString(commonAreaShare)}>
          {commonAreaShare || '-'}
        </Text>
      </TableCell>

      {/* Leieareal beregnet */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedString(calculatedLettableArea)}>
          {calculatedLettableArea || '-'}
        </Text>
        {isDefinedString(commonAreaAddOnFactor) && (
          <Text as="div" className="text-xs opacity-60">
            {tGlobal('agreementSpace.addOnFactor', { value: commonAreaAddOnFactor })}
          </Text>
        )}
      </TableCell>

      {/* Leieareal avtale */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedString(overrideArea)}>
          {overrideArea || '-'}
        </Text>
        {isDefinedString(overrideAreaAddOnFactor) && (
          <Text as="div" className="text-xs opacity-60">
            {tGlobal('agreementSpace.addOnFactor', { value: overrideAreaAddOnFactor })}
          </Text>
        )}
      </TableCell>

      {/* Produktnavn */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedString(productName)}>
          {productName}
        </Text>
      </TableCell>

      {/* Produktpris */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedString(productPrice)}>
          {productPrice}
        </Text>
      </TableCell>

      {/* Overstyrt produktpris */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedString(overwrittenPrice)}>
          {overwrittenPrice || '-'}
        </Text>
      </TableCell>

      {/* Fakturert */}
      <TableCell loading={isFetching}>
        {agreementSpace?.oneTimeTotalAmount != null ? (
          <Text as="div">{agreementSpace.isInvoiced ? tGlobal('yes') : tGlobal('no')}</Text>
        ) : (
          <Text as="div">-</Text>
        )}
      </TableCell>

      {/* Totalt */}
      <TableCell align="right" loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedString(totalAmount)}>
          <strong>{totalAmount}</strong>
        </Text>
      </TableCell>
    </TableRow>
  );
}
